/**
 * 데모 데이터베이스 — localStorage 에 저장되는 사이트 전체 상태.
 *
 * 백엔드 엔티티(site_users · site_orders · site_estimations …)와 같은 필드명을 쓴다.
 * http 모드로 전환해도 화면 코드가 바뀌지 않도록 하기 위해서다.
 *
 * 로그인 계정: config/env.js 의 TEST_ACCOUNT (기본 admin@trinitylaserprofile.com / Trinity#2026)
 */
import { TEST_ACCOUNT } from '../config/env';

const KEY = 'trinity.mockdb.v4';

const DAY = 86400000;
const daysAgo = n => new Date(Date.now() - n * DAY).toISOString();
const dateOnly = n => new Date(Date.now() - n * DAY).toISOString().slice(0, 10);
const dateIn = n => new Date(Date.now() + n * DAY).toISOString().slice(0, 10);

/* ── 소재 마스터 (backend data.sql 과 동일 체계) ─────────────────── */
const GRADES = [
  {
    name: 'Stainless Steel 304', code: 'SS304', category: 'Stainless Steel', density: 7.93, costPerKg: 4.5,
    thicknesses: [0.9, 1.2, 1.5, 2.0, 3.0, 4.0, 5.0, 6.0, 8.0, 10.0, 12.0],
    description: 'General-purpose austenitic stainless. 2B finish, N₂ assist for an oxide-free edge that welds and polishes cleanly.',
    sheet: '3000 × 1500', lead: '2–3 days'
  },
  {
    name: 'Stainless Steel 316', code: 'SS316', category: 'Stainless Steel', density: 7.99, costPerKg: 6.0,
    thicknesses: [1.2, 1.5, 2.0, 3.0, 4.0, 5.0, 6.0, 8.0, 10.0],
    description: 'Marine grade with molybdenum for chloride resistance. Coastal architecture, food and chemical plant.',
    sheet: '3000 × 1500', lead: '2–3 days'
  },
  {
    name: 'Mild Steel', code: 'MS', category: 'Mild Steel', density: 7.85, costPerKg: 1.2,
    thicknesses: [1.0, 1.6, 2.0, 2.5, 3.0, 4.0, 5.0, 6.0, 8.0, 10.0, 12.0, 16.0, 20.0, 25.0],
    description: 'Hot-rolled AS/NZS 1594 HA250 / 3678-250. O₂ assist above 6 mm for a clean square edge on structural work.',
    sheet: '3000 × 1500 · 6000 × 2000', lead: '1–2 days'
  },
  {
    name: 'Zincanneal', code: 'ZA', category: 'Mild Steel', density: 7.85, costPerKg: 1.6,
    thicknesses: [0.8, 1.0, 1.2, 1.6, 2.0, 2.5, 3.0],
    description: 'Zinc-iron alloy coated cold-rolled steel. Paints beautifully — brackets, enclosures and cabinet work.',
    sheet: '2400 × 1200', lead: '1–2 days'
  },
  {
    name: 'Aluminium 5052', code: 'AL5052', category: 'Aluminium', density: 2.68, costPerKg: 3.5,
    thicknesses: [1.0, 1.2, 1.6, 2.0, 2.5, 3.0, 4.0, 5.0, 6.0, 8.0, 10.0],
    description: 'Marine aluminium with excellent formability. Bends without cracking, good corrosion resistance.',
    sheet: '3000 × 1500', lead: '2–3 days'
  },
  {
    name: 'Aluminium 6061-T6', code: 'AL6061', category: 'Aluminium', density: 2.7, costPerKg: 3.8,
    thicknesses: [2.0, 3.0, 4.0, 5.0, 6.0, 8.0, 10.0, 12.0],
    description: 'Heat-treated structural aluminium. Machined parts, jigs, frames — where strength matters more than bending.',
    sheet: '2500 × 1250', lead: '3–4 days'
  },
  {
    name: 'Brass CZ121', code: 'BR', category: 'Copper Alloys', density: 8.5, costPerKg: 14.0,
    thicknesses: [1.0, 1.5, 2.0, 3.0],
    description: 'Free-machining brass for signage, trim and decorative panels. Cut with N₂ for a bright edge.',
    sheet: '2000 × 1000', lead: '4–5 days'
  }
];

function seedMaterials() {
  const rows = [];
  let id = 1;
  GRADES.forEach(g => {
    g.thicknesses.forEach(t => {
      rows.push({
        id: id++,
        name: g.name,
        materialCode: `${g.code}-${t.toFixed(1)}`,
        category: g.category,
        thickness: t,
        density: g.density,
        costPerKg: g.costPerKg,
        available: !(g.code === 'AL6061' && t === 12) && !(g.code === 'MS' && t === 25),
        description: g.description,
        sheetSize: g.sheet,
        leadTime: g.lead
      });
    });
  });
  return rows;
}

/* ── 네스팅 SVG — 시트 위 부품 배치도를 즉석 생성 ────────────────── */
function nestingSvg(sheetW, sheetH, parts) {
  const s = 0.16;
  const w = Math.round(sheetW * s);
  const h = Math.round(sheetH * s);
  const rects = parts
    .map(
      p =>
        `<rect x="${(p.x * s).toFixed(1)}" y="${(p.y * s).toFixed(1)}" width="${(p.w * s).toFixed(1)}" height="${(p.h * s).toFixed(1)}" rx="1.5" fill="#1b4fe8" fill-opacity=".85" stroke="#0a34b8" stroke-width=".8"/>` +
        (p.holes || [])
          .map(hh => `<circle cx="${((p.x + hh.cx) * s).toFixed(1)}" cy="${((p.y + hh.cy) * s).toFixed(1)}" r="${(hh.r * s).toFixed(1)}" fill="#e3e8ef"/>`)
          .join('')
    )
    .join('');
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${w} ${h}" width="${w}" height="${h}"><rect x="0" y="0" width="${w}" height="${h}" fill="#e3e8ef" stroke="#919cad" stroke-width="1"/>${rects}</svg>`;
}

const PARTS_A = [
  { x: 40, y: 40, w: 900, h: 420, holes: [{ cx: 80, cy: 80, r: 18 }, { cx: 820, cy: 80, r: 18 }, { cx: 80, cy: 340, r: 18 }, { cx: 820, cy: 340, r: 18 }] },
  { x: 980, y: 40, w: 900, h: 420, holes: [{ cx: 80, cy: 80, r: 18 }, { cx: 820, cy: 80, r: 18 }, { cx: 80, cy: 340, r: 18 }, { cx: 820, cy: 340, r: 18 }] },
  { x: 1920, y: 40, w: 640, h: 420, holes: [{ cx: 320, cy: 210, r: 90 }] },
  { x: 40, y: 500, w: 560, h: 560, holes: [{ cx: 280, cy: 280, r: 160 }] },
  { x: 640, y: 500, w: 560, h: 560, holes: [{ cx: 280, cy: 280, r: 160 }] },
  { x: 1240, y: 500, w: 1320, h: 260 },
  { x: 1240, y: 800, w: 640, h: 260, holes: [{ cx: 120, cy: 130, r: 30 }, { cx: 520, cy: 130, r: 30 }] },
  { x: 1920, y: 800, w: 640, h: 260, holes: [{ cx: 120, cy: 130, r: 30 }, { cx: 520, cy: 130, r: 30 }] },
  { x: 40, y: 1100, w: 2520, h: 180 },
  { x: 2600, y: 40, w: 360, h: 1240, holes: [{ cx: 180, cy: 200, r: 40 }, { cx: 180, cy: 620, r: 40 }, { cx: 180, cy: 1040, r: 40 }] }
];

const PARTS_B = [
  { x: 40, y: 40, w: 1180, h: 640, holes: [{ cx: 590, cy: 320, r: 220 }] },
  { x: 1260, y: 40, w: 1180, h: 640, holes: [{ cx: 590, cy: 320, r: 220 }] },
  { x: 40, y: 720, w: 780, h: 480 },
  { x: 860, y: 720, w: 780, h: 480 },
  { x: 1680, y: 720, w: 760, h: 480, holes: [{ cx: 100, cy: 100, r: 24 }, { cx: 660, cy: 100, r: 24 }, { cx: 100, cy: 380, r: 24 }, { cx: 660, cy: 380, r: 24 }] },
  { x: 2480, y: 40, w: 480, h: 1160 }
];

/* ── 시드 ─────────────────────────────────────────────────────────── */
function seed() {
  const demo = {
    id: 1,
    name: 'Trinity Laser Profile',
    email: TEST_ACCOUNT.email,
    password: TEST_ACCOUNT.password,
    phone: '0413 431 535',
    company: 'Trinity Laser Profile',
    address: 'Unit 4, 6 Forge St, Blacktown NSW 2148',
    thumbnailUrl: '',
    role: 'USER',
    dateJoined: daysAgo(412),
    createdAt: daysAgo(412)
  };

  const orders = [
    {
      id: 1, orderNumber: 'TL-2025-0412', userId: 1, status: 'IN_PRODUCTION', amount: 1842.6, orderDate: daysAgo(3),
      poNumber: 'PO-8841', quoteNumber: 'Q-2025-0388',
      materials: [
        { materialName: 'Stainless Steel 304', thickness: 2.0, quantity: 24, unit: 'pcs' },
        { materialName: 'Mild Steel', thickness: 5.0, quantity: 8, unit: 'pcs' }
      ],
      timeline: [
        { status: 'PENDING', at: daysAgo(3), note: 'Order received from quote Q-2025-0388' },
        { status: 'CONFIRMED', at: daysAgo(2.6), note: 'Material allocated — SS304 2.0 mm, MS 5.0 mm' },
        { status: 'IN_PRODUCTION', at: daysAgo(1.2), note: 'Nested on 2 sheets · cutting on cell 2' }
      ],
      hasNesting: true, certificateCount: 2, eta: dateIn(2)
    },
    {
      id: 2, orderNumber: 'TL-2025-0398', userId: 1, status: 'SHIPPED', amount: 612.4, orderDate: daysAgo(6),
      poNumber: 'PO-8802', quoteNumber: 'Q-2025-0371',
      materials: [{ materialName: 'Aluminium 5052', thickness: 3.0, quantity: 40, unit: 'pcs' }],
      timeline: [
        { status: 'PENDING', at: daysAgo(6), note: 'Order received' },
        { status: 'CONFIRMED', at: daysAgo(5.7), note: 'Material allocated — AL5052 3.0 mm' },
        { status: 'IN_PRODUCTION', at: daysAgo(4), note: 'Cut on cell 1 · deburred' },
        { status: 'SHIPPED', at: daysAgo(0.5), note: 'Dispatched via TNT · con note 7731 0092' }
      ],
      hasNesting: true, certificateCount: 1, tracking: 'TNT 7731 0092', eta: dateIn(1)
    },
    {
      id: 3, orderNumber: 'TL-2025-0385', userId: 1, status: 'DELIVERED', amount: 2210.0, orderDate: daysAgo(14),
      poNumber: 'PO-8760', quoteNumber: 'Q-2025-0352',
      materials: [
        { materialName: 'Mild Steel', thickness: 10.0, quantity: 12, unit: 'pcs' },
        { materialName: 'Mild Steel', thickness: 6.0, quantity: 30, unit: 'pcs' }
      ],
      timeline: [
        { status: 'PENDING', at: daysAgo(14) },
        { status: 'CONFIRMED', at: daysAgo(13.5) },
        { status: 'IN_PRODUCTION', at: daysAgo(12) },
        { status: 'SHIPPED', at: daysAgo(10), note: 'Dispatched via Trinity truck' },
        { status: 'DELIVERED', at: daysAgo(9.6), note: 'Signed: J. Lee' }
      ],
      hasNesting: false, certificateCount: 2
    },
    {
      id: 4, orderNumber: 'TL-2025-0371', userId: 1, status: 'CONFIRMED', amount: 388.9, orderDate: daysAgo(1),
      poNumber: 'PO-8850', quoteNumber: 'Q-2025-0402',
      materials: [{ materialName: 'Zincanneal', thickness: 1.6, quantity: 60, unit: 'pcs' }],
      timeline: [
        { status: 'PENDING', at: daysAgo(1), note: 'Order received' },
        { status: 'CONFIRMED', at: daysAgo(0.4), note: 'Scheduled for cell 1 — tomorrow AM' }
      ],
      hasNesting: false, certificateCount: 0, eta: dateIn(3)
    },
    {
      id: 5, orderNumber: 'TL-2025-0350', userId: 1, status: 'DELIVERED', amount: 4920.75, orderDate: daysAgo(31),
      poNumber: 'PO-8690', quoteNumber: 'Q-2025-0310',
      materials: [
        { materialName: 'Stainless Steel 316', thickness: 3.0, quantity: 18, unit: 'pcs' },
        { materialName: 'Stainless Steel 316', thickness: 5.0, quantity: 6, unit: 'pcs' }
      ],
      timeline: [
        { status: 'PENDING', at: daysAgo(31) },
        { status: 'CONFIRMED', at: daysAgo(30.5) },
        { status: 'IN_PRODUCTION', at: daysAgo(29) },
        { status: 'SHIPPED', at: daysAgo(27) },
        { status: 'DELIVERED', at: daysAgo(26.5) }
      ],
      hasNesting: false, certificateCount: 3
    },
    {
      id: 6, orderNumber: 'TL-2025-0331', userId: 1, status: 'CANCELLED', amount: 265.0, orderDate: daysAgo(40),
      poNumber: '—', quoteNumber: 'Q-2025-0290',
      materials: [{ materialName: 'Brass CZ121', thickness: 1.5, quantity: 10, unit: 'pcs' }],
      timeline: [
        { status: 'PENDING', at: daysAgo(40) },
        { status: 'CANCELLED', at: daysAgo(39), note: 'Cancelled at customer request — design revision' }
      ],
      hasNesting: false, certificateCount: 0
    },
    {
      id: 7, orderNumber: 'TL-2025-0302', userId: 1, status: 'DELIVERED', amount: 1150.3, orderDate: daysAgo(58),
      poNumber: 'PO-8555', quoteNumber: 'Q-2025-0261',
      materials: [{ materialName: 'Aluminium 6061-T6', thickness: 6.0, quantity: 16, unit: 'pcs' }],
      timeline: [
        { status: 'PENDING', at: daysAgo(58) },
        { status: 'CONFIRMED', at: daysAgo(57) },
        { status: 'IN_PRODUCTION', at: daysAgo(56) },
        { status: 'SHIPPED', at: daysAgo(54) },
        { status: 'DELIVERED', at: daysAgo(53) }
      ],
      hasNesting: false, certificateCount: 1
    },
    {
      id: 8, orderNumber: 'TL-2024-0288', userId: 1, status: 'DELIVERED', amount: 3380.0, orderDate: daysAgo(84),
      poNumber: 'PO-8410', quoteNumber: 'Q-2024-0240',
      materials: [
        { materialName: 'Mild Steel', thickness: 3.0, quantity: 120, unit: 'pcs' },
        { materialName: 'Mild Steel', thickness: 8.0, quantity: 20, unit: 'pcs' }
      ],
      timeline: [
        { status: 'PENDING', at: daysAgo(84) },
        { status: 'CONFIRMED', at: daysAgo(83) },
        { status: 'IN_PRODUCTION', at: daysAgo(81) },
        { status: 'SHIPPED', at: daysAgo(78) },
        { status: 'DELIVERED', at: daysAgo(77) }
      ],
      hasNesting: false, certificateCount: 2
    }
  ];

  const certificates = [
    { id: 1, certNumber: 'MC-25-1187', orderNumber: 'TL-2025-0412', type: 'MILL_CERT', material: 'SS304 2.0 mm · Heat 7X2210', issueDate: dateOnly(2), expirationDate: null, docketNumber: 'DD-25-4412', fileUrl: '#' },
    { id: 2, certNumber: 'MC-25-1188', orderNumber: 'TL-2025-0412', type: 'MILL_CERT', material: 'MS 5.0 mm · Heat 51A889', issueDate: dateOnly(2), expirationDate: null, docketNumber: 'DD-25-4412', fileUrl: '#' },
    { id: 3, certNumber: 'MC-25-1162', orderNumber: 'TL-2025-0398', type: 'MILL_CERT', material: 'AL5052 3.0 mm · Lot 2231', issueDate: dateOnly(5), expirationDate: null, docketNumber: 'DD-25-4398', fileUrl: '#' },
    { id: 4, certNumber: 'MC-25-1140', orderNumber: 'TL-2025-0385', type: 'MILL_CERT', material: 'MS 10.0 mm · Heat 51A702', issueDate: dateOnly(13), expirationDate: null, docketNumber: 'DD-25-4385', fileUrl: '#' },
    { id: 5, certNumber: 'CC-25-0312', orderNumber: 'TL-2025-0385', type: 'COMPLIANCE_CERT', material: 'AS/NZS 3678-250', issueDate: dateOnly(10), expirationDate: dateIn(720), docketNumber: 'DD-25-4385', fileUrl: '#' },
    { id: 6, certNumber: 'MC-25-1098', orderNumber: 'TL-2025-0350', type: 'MILL_CERT', material: 'SS316 3.0 mm · Heat 8K0071', issueDate: dateOnly(30), expirationDate: null, docketNumber: 'DD-25-4350', fileUrl: '#' },
    { id: 7, certNumber: 'MC-25-1099', orderNumber: 'TL-2025-0350', type: 'MILL_CERT', material: 'SS316 5.0 mm · Heat 8K0074', issueDate: dateOnly(30), expirationDate: null, docketNumber: 'DD-25-4350', fileUrl: '#' },
    { id: 8, certNumber: 'TR-25-0087', orderNumber: 'TL-2025-0350', type: 'TEST_REPORT', material: 'SS316 · PMI verification', issueDate: dateOnly(28), expirationDate: null, docketNumber: 'DD-25-4350', fileUrl: '#' },
    { id: 9, certNumber: 'MC-25-1044', orderNumber: 'TL-2025-0302', type: 'MILL_CERT', material: 'AL6061-T6 6.0 mm · Lot 1907', issueDate: dateOnly(56), expirationDate: null, docketNumber: 'DD-25-4302', fileUrl: '#' },
    { id: 10, certNumber: 'MC-24-0977', orderNumber: 'TL-2024-0288', type: 'MILL_CERT', material: 'MS 3.0 mm · Heat 50Z118', issueDate: dateOnly(82), expirationDate: null, docketNumber: 'DD-24-4288', fileUrl: '#' },
    { id: 11, certNumber: 'CC-24-0290', orderNumber: 'TL-2024-0288', type: 'COMPLIANCE_CERT', material: 'AS/NZS 1594 HA250', issueDate: dateOnly(80), expirationDate: dateIn(650), docketNumber: 'DD-24-4288', fileUrl: '#' }
  ];

  const nesting = [
    {
      id: 1, orderNumber: 'TL-2025-0412', sheetWidth: 3000, sheetHeight: 1500, utilization: 0.874, totalParts: 32,
      createdAt: daysAgo(1.3),
      materials: [
        { materialName: 'Stainless Steel 304', thickness: 2.0, sheetCount: 1, utilization: 0.874, partsPlaced: 24, offcuts: [{ size: '420 × 210 mm', area: 0.088 }, { size: '300 × 180 mm', area: 0.054 }], svgData: nestingSvg(3000, 1500, PARTS_A) },
        { materialName: 'Mild Steel', thickness: 5.0, sheetCount: 1, utilization: 0.812, partsPlaced: 8, offcuts: [{ size: '640 × 300 mm', area: 0.192 }], svgData: nestingSvg(3000, 1500, PARTS_B) }
      ]
    },
    {
      id: 2, orderNumber: 'TL-2025-0398', sheetWidth: 3000, sheetHeight: 1500, utilization: 0.791, totalParts: 40,
      createdAt: daysAgo(4.2),
      materials: [
        { materialName: 'Aluminium 5052', thickness: 3.0, sheetCount: 2, utilization: 0.791, partsPlaced: 40, offcuts: [{ size: '900 × 280 mm', area: 0.252 }, { size: '360 × 240 mm', area: 0.086 }], svgData: nestingSvg(3000, 1500, PARTS_B) }
      ]
    }
  ];

  const estimations = [
    {
      id: 1, quoteNumber: 'Q-2025-0402', userId: 1, customerName: demo.name, customerEmail: demo.email,
      status: 'APPROVED', notes: 'Bracket set for cabinet run — please deburr.', createdAt: daysAgo(1.5),
      items: [{ fileName: 'bracket-L60.dxf', material: 'Zincanneal', thickness: 1.6, quantity: 60, width: 120, height: 80, cuttingLength: 486, pierceCount: 3, unitPrice: 6.48, totalPrice: 388.9 }],
      total: 388.9
    },
    {
      id: 2, quoteNumber: 'Q-2025-0397', userId: 1, customerName: demo.name, customerEmail: demo.email,
      status: 'REVIEWED', notes: '', createdAt: daysAgo(2.2),
      items: [
        { fileName: 'gusset-plate-8.dxf', material: 'Mild Steel', thickness: 8.0, quantity: 14, width: 260, height: 260, cuttingLength: 1120, pierceCount: 2, unitPrice: 31.2, totalPrice: 436.8 },
        { fileName: 'base-flange.dxf', material: 'Mild Steel', thickness: 12.0, quantity: 4, width: 380, height: 380, cuttingLength: 1780, pierceCount: 5, unitPrice: 92.5, totalPrice: 370.0 }
      ],
      total: 806.8
    },
    {
      id: 3, quoteNumber: 'Q-2025-0388', userId: 1, customerName: demo.name, customerEmail: demo.email,
      status: 'APPROVED', notes: 'Converted to order TL-2025-0412', createdAt: daysAgo(3.4),
      items: [
        { fileName: 'panel-A.dxf', material: 'Stainless Steel 304', thickness: 2.0, quantity: 24, width: 900, height: 420, cuttingLength: 2940, pierceCount: 5, unitPrice: 54.1, totalPrice: 1298.4 },
        { fileName: 'foot-plate.dxf', material: 'Mild Steel', thickness: 5.0, quantity: 8, width: 560, height: 560, cuttingLength: 2240, pierceCount: 2, unitPrice: 68.0, totalPrice: 544.2 }
      ],
      total: 1842.6
    },
    {
      id: 4, quoteNumber: 'Q-2025-0366', userId: 1, customerName: demo.name, customerEmail: demo.email,
      status: 'REJECTED', notes: 'Superseded by revised drawing', createdAt: daysAgo(9),
      items: [{ fileName: 'cover-rev-A.dxf', material: 'Aluminium 5052', thickness: 2.0, quantity: 10, width: 300, height: 200, cuttingLength: 1180, pierceCount: 6, unitPrice: 14.2, totalPrice: 142.0 }],
      total: 142.0
    }
  ];

  const notifications = [
    { id: 1, userId: 1, type: 'ORDER_UPDATE', title: 'TL-2025-0412 is in production', message: 'Your parts are nested on 2 sheets and cutting on cell 2. Expected dispatch in 2 days.', isRead: false, createdAt: daysAgo(0.05), link: '/mypage/orders/TL-2025-0412' },
    { id: 2, userId: 1, type: 'ESTIMATION_REPLY', title: 'Quote Q-2025-0397 reviewed', message: 'An estimator has reviewed your 12 mm flange — pricing updated. Approve to convert to an order.', isRead: false, createdAt: daysAgo(0.3), link: '/mypage/quotes/Q-2025-0397' },
    { id: 3, userId: 1, type: 'ORDER_UPDATE', title: 'TL-2025-0398 dispatched', message: 'Shipped via TNT, con note 7731 0092. Delivery expected tomorrow.', isRead: false, createdAt: daysAgo(0.5), link: '/mypage/orders/TL-2025-0398' },
    { id: 4, userId: 1, type: 'CERTIFICATE_READY', title: 'Mill certificates ready — TL-2025-0412', message: 'MC-25-1187 and MC-25-1188 are attached to docket DD-25-4412.', isRead: true, createdAt: daysAgo(2), link: '/mypage/certificates' },
    { id: 5, userId: 1, type: 'ESTIMATION_REPLY', title: 'Quote Q-2025-0402 approved', message: 'Your bracket set has been approved and converted to order TL-2025-0371.', isRead: true, createdAt: daysAgo(1), link: '/mypage/orders/TL-2025-0371' },
    { id: 6, userId: 1, type: 'SYSTEM', title: 'Scheduled maintenance', message: 'The customer portal will be unavailable Sunday 02:00–04:00 AEST for upgrades.', isRead: true, createdAt: daysAgo(4), link: '' },
    { id: 7, userId: 1, type: 'ORDER_UPDATE', title: 'TL-2025-0385 delivered', message: 'Signed for by J. Lee. Thanks for your order!', isRead: true, createdAt: daysAgo(9.6), link: '/mypage/orders/TL-2025-0385' },
    { id: 8, userId: 1, type: 'CERTIFICATE_READY', title: 'Compliance certificate issued', message: 'CC-25-0312 (AS/NZS 3678-250) is available for order TL-2025-0385.', isRead: true, createdAt: daysAgo(10), link: '/mypage/certificates' }
  ];

  return {
    version: 3,
    seq: { user: 2, order: 9, estimation: 5, notification: 9, inquiry: 1 },
    users: [demo],
    sessions: {},
    orders,
    certificates,
    nesting,
    estimations,
    notifications,
    materials: seedMaterials(),
    inquiries: []
  };
}

/* ── 저장소 ───────────────────────────────────────────────────────── */
let cache = null;

export function loadDb() {
  if (cache) return cache;
  try {
    const raw = window.localStorage.getItem(KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && parsed.version === 3) {
        cache = parsed;
        return cache;
      }
    }
  } catch (e) {
    /* 손상된 저장소는 버리고 다시 시드한다 */
  }
  cache = seed();
  saveDb();
  return cache;
}

export function saveDb() {
  if (!cache) return;
  try {
    window.localStorage.setItem(KEY, JSON.stringify(cache));
  } catch (e) {
    /* 저장 공간 부족 등 — 메모리 상태로 계속 동작 */
  }
}

export function resetDb() {
  cache = seed();
  saveDb();
  return cache;
}

